// Minimal profile page: user info, current plan, manage-subscription button.
import { useTranslation } from 'react-i18next';
import { useUser, useCreatePortal } from '../hooks/useApi';
import { PLAN_DETAILS } from '../types';
import { useFormatDate } from '../hooks/useFormatDate';

export function ProfilePage() {
  const { t } = useTranslation('settings');
  const { data: user, isLoading } = useUser();
  const { mutate: createPortal, isPending: portalPending } = useCreatePortal();
  const formatDate = useFormatDate();

  if (isLoading) {
    return <div className="p-8 text-gray-500">{t('loading')}</div>;
  }

  if (!user) return null;

  const planDetails = PLAN_DETAILS[user.plan];

  return (
    <div className="page-narrow space-y-6 p-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('profile.title')}</h1>
        <p className="mt-2 text-gray-600">{t('profile.subtitle')}</p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
        <ProfileRow label={t('profile.name')} value={user.name || '—'} />
        <ProfileRow label={t('profile.email')} value={user.email} />
        <ProfileRow label={t('profile.plan')} value={planDetails.name} />
        <ProfileRow label={t('profile.memberSince')} value={formatDate(user.createdAt ?? 0)} />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold">{t('subscription.title')}</h2>
        <p className="mt-2 text-sm text-gray-600">{t('subscription.description')}</p>
        <button
          type="button"
          onClick={() => createPortal()}
          disabled={portalPending}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {portalPending ? t('subscription.opening') : t('subscription.manage')}
        </button>
      </div>
    </div>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-6 py-4">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}
